import { useState, useEffect, useMemo } from 'react'
import { publicAPI } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorMessage from '../components/ErrorMessage'
import PostnummerFilter from '../components/PostnummerFilter'
import ExportButtons from '../components/ExportButtons'
import type { ColumnDefinition, ExportMetadata } from '../utils/exportToCsv'
import DateFilterBar, { OutputMode, getDateRangeForMode, formatNorwegianDate } from '../components/DateFilterBar'
import { useYearDates } from '../contexts/YearContext'
import { useVehicleFilter } from '../contexts/VehicleFilterContext'
import PeriodInfoBanner from '../components/PeriodInfoBanner'
import { useMaxDataDate } from '../hooks/useMaxDataDate'
import { BarChart3, TrendingUp, Layers, TrendingDown, Percent, Car } from 'lucide-react'
import ChangeIndicator, { ChangeCount } from '../components/ChangeIndicator'
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import {
  SEGMENT_COLORS,
  SEGMENT_DISPLAY_ORDER,
  type SegmentOverviewResponse,
  type SegmentOverviewRow,
  type SegmentTopModelsResponse,
  type SegmentTopModelsRow,
} from '../types/segments'
import { usePersistedState } from '../hooks/usePersistedState'
import { useMinePostnummer } from '../hooks/useMinePostnummer'
import { useTranslation } from 'react-i18next'
import { localizeApiDetail } from '../i18n/apiError'

// Re-export for backwards compatibility
export { SEGMENT_COLORS } from '../types/segments'
export type {
  SegmentOverviewPeriod,
  SegmentOverviewSummary,
  SegmentOverviewRow,
  SegmentOverviewResponse,
  SegmentTopModelsSummary,
  SegmentTopModelsRow,
  SegmentTopModelsResponse,
} from '../types/segments'

const segmentColor = (id: number): string => SEGMENT_COLORS[id] || '#6b7280'

const fmtInt = (n: number): string => n.toLocaleString('nb-NO')
const fmtPct = (n: number | null | undefined): string =>
  n === null || n === undefined ? '—' : `${n.toFixed(1)}%`
const fmtSigned = (n: number): string => (n >= 0 ? `+${fmtInt(n)}` : fmtInt(n))
const yoyClass = (n: number | null | undefined): string => {
  if (n === null || n === undefined) return 'text-gray-600 dark:text-gray-400'
  return n >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
}

export default function SegmentAnalytics() {
  const { t } = useTranslation('analytics')
  const [activeTab, setActiveTab] = useState<'overview' | number>('overview')

  // Data
  const [overview, setOverview] = useState<SegmentOverviewResponse | null>(null)
  const [segmentData, setSegmentData] = useState<Record<number, SegmentTopModelsResponse>>({})
  const [loading, setLoading] = useState(true)
  const [segmentLoading, setSegmentLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Filter state (dates from context)
  const { startDate, setStartDate, endDate, setEndDate, maxSvvDate } = useYearDates('segment-analytics')

  // DateFilterBar
  const [outputMode, setOutputMode] = usePersistedState<OutputMode>('bp:filter:outputMode', 'year_to_date')
  const [customStartDate, setCustomStartDate] = usePersistedState('bp:filter:customStartDate', '')
  const [customEndDate, setCustomEndDate] = usePersistedState('bp:filter:customEndDate', '')
  const maxDataDate = useMaxDataDate()

  // Postal filter
  const [postnummerFilterEnabled, setPostnummerFilterEnabled] = usePersistedState('segment-analytics:postnummerFilterEnabled', false)
  const minePostnummer = useMinePostnummer(postnummerFilterEnabled)

  // Vehicle filter from global context — segments only apply to personbil
  const { vehicleFilter, setVehicleFilter, getVarebilParam } = useVehicleFilter()

  useEffect(() => {
    if (vehicleFilter !== 'personbil') {
      setVehicleFilter('personbil')
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const [dateRangeAlert, setDateRangeAlert] = useState<string | null>(null)

  const formatDate = (date: Date): string => date.toISOString().split('T')[0]

  const validateDateRange = (start: Date, end: Date): boolean => {
    const diffDays = Math.ceil(Math.abs(end.getTime() - start.getTime()) / 86400000)
    if (diffDays > 366) {
      setDateRangeAlert(t('common.dateRangeTooLarge', { days: diffDays }))
      return false
    }
    setDateRangeAlert(null)
    return true
  }

  // Effective postal code value used for fetches
  const effectivePostnummer = postnummerFilterEnabled && minePostnummer ? minePostnummer : undefined
  const varebilParam = getVarebilParam()

  const filtersKey = useMemo(
    () =>
      [
        formatDate(startDate),
        formatDate(endDate),
        effectivePostnummer ?? '',
        varebilParam === undefined ? '' : String(varebilParam),
      ].join('|'),
    [startDate, endDate, effectivePostnummer, varebilParam]
  )

  // Effect 1: fetch overview when filtersKey changes
  useEffect(() => {
    let cancelled = false
    const run = async () => {
      try {
        setLoading(true)
        setError(null)
        setSegmentData({})
        const data = await publicAPI.getSegmentsOverview({
          start_date: formatDate(startDate),
          end_date: formatDate(endDate),
          mine_postnummer: effectivePostnummer,
          varebil: varebilParam,
        })
        if (cancelled) return
        setOverview(data)
      } catch (err: any) {
        if (cancelled) return
        console.error('Segment overview error:', err)
        setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('segment.loadFailed'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
    // filtersKey is memoized from all filter inputs, so depending on it captures the intent
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey])

  // Effect 2: lazy-load per-segment data
  useEffect(() => {
    if (activeTab === 'overview') return
    if (segmentData[activeTab] !== undefined) return
    const segId = activeTab
    let cancelled = false
    const run = async () => {
      try {
        setSegmentLoading(true)
        const data = await publicAPI.getSegmentTopModelsTyped({
          segment_id: segId,
          start_date: formatDate(startDate),
          end_date: formatDate(endDate),
          mine_postnummer: effectivePostnummer,
          varebil: varebilParam,
          limit: 50,
        })
        if (cancelled) return
        setSegmentData((prev) => ({ ...prev, [segId]: data }))
      } catch (err: any) {
        if (cancelled) return
        console.error('Segment top-models error:', err)
        setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('segment.loadFailed'))
      } finally {
        if (!cancelled) setSegmentLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
    // filtersKey is memoized from all filter inputs, so depending on it captures the intent
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filtersKey])

  const handleApplyFilter = () => {
    if (outputMode === 'custom') {
      if (customStartDate && customEndDate) {
        const s = new Date(customStartDate)
        const e = new Date(customEndDate)
        if (!isNaN(s.getTime()) && !isNaN(e.getTime()) && validateDateRange(s, e)) {
          setStartDate(s)
          setEndDate(e)
        }
      }
    } else {
      const { startDate: sd, endDate: ed } = getDateRangeForMode(outputMode, maxSvvDate ?? undefined)
      setStartDate(new Date(sd))
      setEndDate(new Date(ed))
      setDateRangeAlert(null)
    }
  }

  const handleOutputModeChange = (mode: OutputMode) => {
    setOutputMode(mode)
    if (mode !== 'custom') {
      const { startDate: sd, endDate: ed } = getDateRangeForMode(mode, maxSvvDate ?? undefined)
      setStartDate(new Date(sd))
      setEndDate(new Date(ed))
      setDateRangeAlert(null)
    }
  }

  // Segments ordered logically for the tab strip (size progression, then special)
  const sortedSegments: SegmentOverviewRow[] = useMemo(() => {
    if (!overview) return []
    const byId = new Map(overview.data.map((s) => [s.segment_id, s]))
    // Use display order; include all segments even if count is 0 in this period
    return SEGMENT_DISPLAY_ORDER
      .filter((id) => byId.has(id))
      .map((id) => byId.get(id)!)
  }, [overview])

  // Shared export metadata fields (date range + active filters)
  const exportDateRange = t('common.dateRange', { start: formatNorwegianDate(startDate), end: formatNorwegianDate(endDate) })
  const vehicleFilterLabel =
    vehicleFilter === 'personbil'
      ? t('common.filterPersonbil')
      : vehicleFilter === 'varebil'
      ? t('common.filterVarebil')
      : t('segment.filterAll')
  const exportFilters: string[] = [
    vehicleFilterLabel,
    effectivePostnummer ? t('common.postnummerFilter', { value: effectivePostnummer }) : '',
  ].filter((f) => f)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{t('segment.title')}</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('segment.subtitle')}
        </p>
      </div>

      {/* Filters */}
      <DateFilterBar
        outputMode={outputMode}
        onOutputModeChange={handleOutputModeChange}
        customStartDate={customStartDate}
        onCustomStartDateChange={setCustomStartDate}
        customEndDate={customEndDate}
        onCustomEndDateChange={setCustomEndDate}
        onApply={handleApplyFilter}
        bottomContent={
          <PostnummerFilter
            enabled={postnummerFilterEnabled}
            onToggle={setPostnummerFilterEnabled}
            compact
          />
        }
      />

      {dateRangeAlert && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-800 dark:text-red-200">
            <strong>{t('common.errorPrefix')}</strong> {dateRangeAlert}
          </p>
        </div>
      )}

      <PeriodInfoBanner
        maxDataDate={maxDataDate}
        outputMode={outputMode}
        periodLabel={t(`outputModes.${outputMode}`)}
        startDate={startDate.toISOString().split('T')[0]}
        endDate={endDate.toISOString().split('T')[0]}
      />

      {error && <ErrorMessage message={error} />}

      {loading && !overview ? (
        <div className="card p-8 text-center">
          <LoadingSpinner />
        </div>
      ) : overview ? (
        <>
          {/* Tab strip */}
          <div className="flex flex-wrap gap-2 border-b border-gray-200 dark:border-dark-border pb-2">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-2 text-[13px] font-medium rounded-md transition-colors whitespace-nowrap ${
                activeTab === 'overview'
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                  : 'bg-gray-50 text-gray-600 dark:bg-dark-card dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-hover'
              }`}
            >
              <span className="flex items-center gap-2">
                <Layers className="w-4 h-4" />
                {t('segment.overview')}
              </span>
            </button>
            {sortedSegments.map((s) => (
              <button
                key={s.segment_id}
                onClick={() => setActiveTab(s.segment_id)}
                className={`px-4 py-2 text-[13px] font-medium rounded-md transition-colors whitespace-nowrap ${
                  activeTab === s.segment_id
                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                    : 'bg-gray-50 text-gray-600 dark:bg-dark-card dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-hover'
                }`}
              >
                <span className="flex items-center gap-2">
                  <span
                    className="w-3 h-3 rounded-full inline-block"
                    style={{ backgroundColor: segmentColor(s.segment_id) }}
                  />
                  {s.segment_name}
                </span>
              </button>
            ))}
          </div>

          {activeTab === 'overview' ? (
            <OverviewTab
              overview={overview}
              onSelectSegment={(id) => setActiveTab(id)}
              exportDateRange={exportDateRange}
              exportFilters={exportFilters}
            />
          ) : (
            <SegmentTabPanel
              segmentId={activeTab}
              data={segmentData[activeTab]}
              loading={segmentLoading}
              exportDateRange={exportDateRange}
              exportFilters={exportFilters}
            />
          )}
        </>
      ) : null}
    </div>
  )
}

// ---------- Overview Tab ----------

function OverviewTab({
  overview,
  onSelectSegment,
  exportDateRange,
  exportFilters,
}: {
  overview: SegmentOverviewResponse
  onSelectSegment: (segmentId: number) => void
  exportDateRange: string
  exportFilters: string[]
}) {
  const { t } = useTranslation('analytics')
  const { summary, data } = overview
  const sorted = useMemo(() => {
    const byId = new Map(data.map((s) => [s.segment_id, s]))
    return SEGMENT_DISPLAY_ORDER.filter((id) => byId.has(id)).map((id) => byId.get(id)!)
  }, [data])

  const overviewColumns: ColumnDefinition[] = [
    { key: 'segment_name', label: 'Segment' },
    { key: 'count', label: t('common.count'), format: (v: number) => fmtInt(v) },
    {
      key: 'market_share',
      label: t('segment.share'),
      format: (v: number) => `${v.toFixed(1)}%`,
    },
    { key: 'count_ly', label: t('segment.lastYear'), format: (v: number) => fmtInt(v) },
    {
      key: 'yoy_pct',
      label: t('common.changePct'),
      format: (v: number | null | undefined) => fmtPct(v),
    },
  ]

  const overviewExportMeta: ExportMetadata = {
    title: t('exportTitles.segmentsOverview'),
    dateRange: exportDateRange,
    filters: exportFilters,
  }

  return (
    <div className="space-y-6">
      {/* Export row */}
      <div className="flex justify-end">
        <ExportButtons
          data={sorted}
          columns={overviewColumns}
          metadata={overviewExportMeta}
        />
      </div>

      {/* KPI header */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
              <Car className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400">{t('segment.totalRegistrations')}</p>
              <p
                className="text-xl font-bold text-gray-900 dark:text-gray-100"
                data-testid="segment-total-count"
              >
                {fmtInt(summary.total_count)}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              {summary.yoy_abs >= 0 ? (
                <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
              ) : (
                <TrendingDown className="w-5 h-5 text-red-600 dark:text-red-400" />
              )}
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400">{t('common.change')}</p>
              <p className={`text-xl font-bold ${yoyClass(summary.yoy_abs)}`}>
                {fmtSigned(summary.yoy_abs)}{' '}
                <span className="text-sm font-semibold">
                  ({fmtPct(summary.yoy_pct)})
                </span>
              </p>
            </div>
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <Percent className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400">{t('segment.leasingShare')}</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {summary.leasing_pct.toFixed(1)}%
              </p>
            </div>
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
              <Layers className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400">{t('segment.segmentCount')}</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {summary.segment_count}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Two-column body: Pie + Table */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            {t('segment.distribution')}
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={sorted}
                dataKey="count"
                nameKey="segment_name"
                cx="50%"
                cy="50%"
                outerRadius={140}
                label={(entry: any) => entry.segment_name}
                onClick={(e: any) => {
                  if (e && e.segment_id !== undefined) onSelectSegment(e.segment_id)
                }}
              >
                {sorted.map((row) => (
                  <Cell
                    key={row.segment_id}
                    fill={segmentColor(row.segment_id)}
                    style={{ cursor: 'pointer' }}
                  />
                ))}
              </Pie>
              <Tooltip formatter={(v: any) => fmtInt(Number(v))} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="card overflow-x-auto">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 p-6 pb-2">
            {t('segment.allSegments')}
          </h3>
          <table className="data-table data-table-compact">
            <thead>
              <tr>
                <th>Segment</th>
                <th className="text-right">{t('common.count')}</th>
                <th className="text-right">{t('segment.share')}</th>
                <th className="text-right">{t('segment.lastYear')}</th>
                <th className="text-right">{t('common.changePct')}</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => (
                <tr
                  key={row.segment_id}
                  onClick={() => onSelectSegment(row.segment_id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onSelectSegment(row.segment_id)
                    }
                  }}
                  className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <td>
                    <div className="flex items-center gap-2">
                      <span
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: segmentColor(row.segment_id) }}
                      />
                      <span className="font-semibold">{row.segment_name}</span>
                    </div>
                  </td>
                  <td className="text-right">{fmtInt(row.count)}</td>
                  <td className="text-right">{row.market_share.toFixed(1)}%</td>
                  <td className="text-right">{fmtInt(row.count_ly)}</td>
                  <td className={`text-right font-semibold ${yoyClass(row.yoy_pct)}`}>
                    {fmtPct(row.yoy_pct)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// ---------- Per-Segment Tab Panel ----------

type SortKey =
  | 'rank'
  | 'brand_name'
  | 'master_model'
  | 'count'
  | 'segment_share_pct'
  | 'count_ly'
  | 'yoy_abs'
  | 'yoy_pct'
  | 'leasing_pct'

type SortDir = 'asc' | 'desc'

function SegmentTabPanel({
  segmentId,
  data,
  loading,
  exportDateRange,
  exportFilters,
}: {
  segmentId: number
  data: SegmentTopModelsResponse | undefined
  loading: boolean
  exportDateRange: string
  exportFilters: string[]
}) {
  const { t } = useTranslation('analytics')
  const [sortKey, setSortKey] = useState<SortKey>('count')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  const rows = useMemo(() => {
    if (!data) return []
    const copy = [...data.data]
    copy.sort((a, b) => {
      const av = a[sortKey]
      const bv = b[sortKey]
      if (av === null || av === undefined) return 1
      if (bv === null || bv === undefined) return -1
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av
      }
      const as = String(av)
      const bs = String(bv)
      return sortDir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as)
    })
    return copy
  }, [data, sortKey, sortDir])

  if (loading && !data) {
    return (
      <div className="card p-8 text-center">
        <LoadingSpinner />
      </div>
    )
  }

  if (!data) return null

  const s = data.segment_summary

  const sortArrow = (key: SortKey) =>
    sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''

  const Th = ({ k, label, align }: { k: SortKey; label: string; align?: 'right' | 'left' | 'center' }) => (
    <th
      className={`cursor-pointer select-none ${align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : ''}`}
      onClick={() => handleSort(k)}
    >
      {label}
      {sortArrow(k)}
    </th>
  )

  const segmentColumns: ColumnDefinition[] = [
    { key: 'rank', label: t('common.rank') },
    { key: 'brand_name', label: t('common.brand'), format: (v, row) => String(v ?? row?.brand_key ?? '') },
    { key: 'master_model', label: t('common.model') },
    { key: 'count', label: t('common.count'), format: (v: number) => fmtInt(v) },
    {
      key: 'segment_share_pct',
      label: t('segment.shareInSegment'),
      format: (v: number) => `${v.toFixed(1)}%`,
    },
    { key: 'count_ly', label: t('segment.countLastYear'), format: (v: number) => fmtInt(v) },
    { key: 'yoy_abs', label: t('common.change'), format: (v: number) => fmtSigned(v) },
    { key: 'yoy_pct', label: t('common.changePct'), format: (v: number | null | undefined) => fmtPct(v) },
    { key: 'leasing_pct', label: 'Leasing %', format: (v: number) => `${v.toFixed(1)}%` },
  ]

  const slug = s.segment_name.toLowerCase().replace(/\s+/g, '-')
  const segmentExportMeta: ExportMetadata = {
    title: `segment_${slug}`,
    dateRange: exportDateRange,
    filters: exportFilters,
  }

  return (
    <div className="space-y-6" data-testid="segment-tab-panel">
      {/* Export row */}
      <div className="flex justify-end">
        <ExportButtons
          data={rows}
          columns={segmentColumns}
          metadata={segmentExportMeta}
          disabled={rows.length === 0}
        />
      </div>

      {/* Segment header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-3">
          <span
            className="w-4 h-4 rounded-full"
            style={{ backgroundColor: segmentColor(segmentId) }}
          />
          {s.segment_name}
        </h2>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="card p-4">
          <p className="text-xs text-gray-600 dark:text-gray-400">{t('segment.countInSegment')}</p>
          <p className="text-xl font-bold text-gray-900 dark:text-gray-100 mt-1">
            {fmtInt(s.segment_count)}
          </p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-gray-600 dark:text-gray-400">{t('segment.shareOfTotal')}</p>
          <p className="text-xl font-bold text-gray-900 dark:text-gray-100 mt-1">
            {s.market_share_of_total.toFixed(1)}%
          </p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-gray-600 dark:text-gray-400">{t('common.change')}</p>
          <p className={`text-xl font-bold mt-1 ${yoyClass(s.yoy_abs)}`}>
            {fmtSigned(s.yoy_abs)}{' '}
            <span className="text-sm">({fmtPct(s.yoy_pct)})</span>
          </p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-gray-600 dark:text-gray-400">{t('segment.leasingShare')}</p>
          <p className="text-xl font-bold text-gray-900 dark:text-gray-100 mt-1">
            {s.leasing_pct.toFixed(1)}%
          </p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-gray-600 dark:text-gray-400">{t('common.modelCount')}</p>
          <p className="text-xl font-bold text-gray-900 dark:text-gray-100 mt-1">
            {s.model_count}
          </p>
        </div>
      </div>

      {/* Models grid */}
      {rows.length === 0 ? (
        <div className="card p-8 text-center text-gray-600 dark:text-gray-400">
          {t('segment.noRegistrations')}
        </div>
      ) : (
        <div className="card overflow-x-auto">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 p-6 pb-2">
            {t('segment.modelsIn', { segment: s.segment_name })}
          </h3>
          <table className="data-table data-table-compact">
            <thead>
              <tr>
                <Th k="rank" label={t('common.rank')} align="center" />
                <Th k="brand_name" label={t('common.brand')} />
                <Th k="master_model" label={t('common.model')} />
                <Th k="count" label={t('common.count')} align="right" />
                <Th k="segment_share_pct" label={t('segment.shareInSegmentPct')} align="right" />
                <Th k="count_ly" label={t('segment.countLastYear')} align="right" />
                <Th k="yoy_abs" label={t('common.change')} align="right" />
                <Th k="yoy_pct" label={t('common.changePct')} align="right" />
                <Th k="leasing_pct" label="Leasing %" align="right" />
              </tr>
            </thead>
            <tbody>
              {/* Summary / TOTAL row */}
              <tr className="bg-primary-50 dark:bg-primary-900/20 font-bold border-t border-b border-primary-300 dark:border-primary-600">
                <td className="text-center py-2">
                  <span className="text-primary-600 dark:text-primary-400 font-bold">∑</span>
                </td>
                <td className="py-2"></td>
                <td className="max-w-[180px] truncate font-bold text-primary-700 dark:text-primary-300 py-2">
                  TOTAL {s.segment_name}
                </td>
                <td className="text-right font-bold py-2">{fmtInt(s.segment_count)}</td>
                <td className="text-right py-2">
                  <span className="font-bold text-primary-600 dark:text-primary-400">100,0</span>
                </td>
                <td className="text-right font-bold py-2">{fmtInt(s.segment_count_ly)}</td>
                <td className="text-right font-bold py-2">
                  <ChangeCount value={s.yoy_abs} />
                </td>
                <td className="text-right font-bold py-2">
                  <ChangeIndicator value={s.yoy_pct} />
                </td>
                <td className="text-right font-bold py-2">{s.leasing_pct.toFixed(1)}</td>
              </tr>
              {rows.map((row: SegmentTopModelsRow) => (
                <tr key={row.master_model_id}>
                  <td className="text-center">{row.rank}</td>
                  <td className="font-semibold">{row.brand_name || row.brand_key}</td>
                  <td>{row.master_model}</td>
                  <td className="text-right">{fmtInt(row.count)}</td>
                  <td className="text-right">{row.segment_share_pct.toFixed(1)}</td>
                  <td className="text-right">{fmtInt(row.count_ly)}</td>
                  <td className="text-right">
                    <ChangeCount value={row.yoy_abs} />
                  </td>
                  <td className="text-right">
                    <ChangeIndicator value={row.yoy_pct} />
                  </td>
                  <td className="text-right">{row.leasing_pct.toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
